import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Science as ScienceIcon,
  Person as PersonIcon,
  Dashboard as OperationsIcon,
  Logout as LogoutIcon,
  MenuBook as ApiDocsIcon,
  HelpOutline as HelpOutlineIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  ViewSidebar as DrawerIcon,
  SupportAgent as ServiceCenterIcon,
  AdminPanelSettings as ControlCenterIcon,
} from '@mui/icons-material';
import SystemMotdDialog from './SystemMotdDialog';
import CommandPalette from './operator/CommandPalette';
import AdminDrawer from './operator/AdminDrawer';
import ContactSupportDialog from './ContactSupportDialog';
import FloatingIframeWindow from './operator/FloatingIframeWindow';
import { useAuth } from '../contexts/AuthContext';
import { useApiAccess } from '../hooks/useApiAccess';
import { apiService } from '../services/apiService';
import { websocketService } from '../services/websocketService';
import { getAccessToken } from '../services/authStorage';

// Minimal layout for the Admin Control Center. Uses the same dense dark top
// bar style as the Operations view (OperatorLayout), but the left side shows
// the "Asterism" brand + info links instead of the observatory
// selector. No 240px legacy navigation drawer.
const formatUtcParts = (d) => {
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const da = String(d.getUTCDate()).padStart(2, '0');
  const yr = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return { time: `${hh}:${mm}:${ss}`, date: `${mo}-${da}-${yr}` };
};

const UtcClock = ({ now }) => {
  const { time, date } = formatUtcParts(now);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1 }}>
        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1 }}>
          {time}
        </Typography>
        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.1, mt: 0.25 }}>
          {date}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', alignSelf: 'center' }}>
        UTC
      </Typography>
    </Box>
  );
};

const ControlCenterLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const apiAccessAllowed = useApiAccess();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [floatingWindows, setFloatingWindows] = useState({});
  const [memberships, setMemberships] = useState([]);
  const [orgMemberships, setOrgMemberships] = useState([]);

  useEffect(() => {
    try {
      if (!websocketService.isConnected) websocketService.connect();
    } catch (e) { /* ignore */ }

    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      // Singleton WS is shared — don't disconnect here.
      clearInterval(timeInterval);
    };
  }, []);

  // Browser tab title
  useEffect(() => {
    const prev = document.title;
    document.title = 'Asterism - Administration';
    return () => { document.title = prev; };
  }, []);

  // User memberships for the navigation drawer
  useEffect(() => {
    if (!user?._id && !user?.id) return;
    const userId = user._id || user.id;
    apiService.getUserMemberships(userId).then((res) => {
      if (res?.success) setMemberships(res.data || []);
    }).catch(() => {});
    apiService.getUserOrganizations().then((res) => {
      setOrgMemberships(res.data?.organizations || res.data || []);
    }).catch(() => {});
  }, [user]);

  // Ctrl+K command palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';
  const isServerAdmin = user?.role === 'server_admin';

  // This layout backs both the Admin Control Center and the Service Center, so
  // the avatar menu must omit whichever of the two is currently active — the
  // active UI should never offer to navigate to itself.
  const onServiceCenter = location.pathname.startsWith('/admin/service-center');
  const onControlCenter = location.pathname.startsWith('/admin/control-center');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openApiDocs = () => {
    const token = getAccessToken();
    const url = token ? `/api/docs?token=${token}` : '/api/docs';
    window.open(url, '_blank', 'noopener');
  };

  const navLinkSx = {
    color: 'rgba(255,255,255,0.7)',
    textTransform: 'none',
    fontSize: '0.75rem',
    fontWeight: 500,
    minWidth: 0,
    px: 1,
    '&:hover': { color: 'rgba(255,255,255,1)', backgroundColor: 'rgba(255,255,255,0.04)' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#1a1a1a',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 48, gap: 1.5 }}>
          {/* Brand — clickable → /about */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.85 },
            }}
            onClick={() => navigate('/about')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/about'); }}
          >
            <ScienceIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography
              variant="subtitle2"
              noWrap
              sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }}
            >
              Asterism
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* UTC clock — time with date smaller below, UTC centered to the right */}
          <UtcClock now={currentTime} />

          {/* About / API Docs / Support — to the right of the UTC clock,
              same order in both the Operations and Control Center layouts. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <Button size="small" startIcon={<InfoIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/about')} sx={navLinkSx}>
              About
            </Button>
            {apiAccessAllowed && (
              <Button size="small" startIcon={<ApiDocsIcon sx={{ fontSize: 14 }} />} onClick={openApiDocs} sx={navLinkSx}>
                API Docs
              </Button>
            )}
            <Button size="small" startIcon={<HelpOutlineIcon sx={{ fontSize: 14 }} />} onClick={() => setSupportOpen(true)} sx={navLinkSx}>
              Support
            </Button>
          </Box>

          {/* Command palette trigger */}
          <Tooltip title="Command Palette (Ctrl+K)">
            <IconButton size="small" onClick={() => setPaletteOpen(true)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Admin drawer trigger */}
          <Tooltip title="Navigation">
            <IconButton size="small" onClick={() => setDrawerOpen(true)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
              <DrawerIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* User block */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5 }}>
            {isAdmin && (
              <Chip label="Admin" size="small" color="secondary" sx={{ height: 20, fontSize: '0.6rem' }} />
            )}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {user?.name || user?.email}
            </Typography>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0 }}>
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                {user?.name?.[0] || user?.email?.[0] || 'U'}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => {
              const winId = `float_${Date.now()}`;
              setFloatingWindows(prev => ({
                ...prev,
                [winId]: {
                  url: '/profile?standalone=1',
                  title: 'Profile',
                  width: 960,
                  height: 720,
                },
              }));
              setAnchorEl(null);
            }}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { navigate('/operations'); setAnchorEl(null); }}>
              <ListItemIcon><OperationsIcon fontSize="small" /></ListItemIcon>
              Operations View
            </MenuItem>
            {!onControlCenter && (
              <MenuItem onClick={() => { navigate('/admin/control-center'); setAnchorEl(null); }}>
                <ListItemIcon><ControlCenterIcon fontSize="small" /></ListItemIcon>
                Admin Control Center
              </MenuItem>
            )}
            {isServerAdmin && !onServiceCenter && (
              <MenuItem onClick={() => { navigate('/admin/service-center'); setAnchorEl(null); }}>
                <ListItemIcon><ServiceCenterIcon fontSize="small" /></ListItemIcon>
                Service Center
              </MenuItem>
            )}
            <MenuItem onClick={() => { navigate('/dashboard'); setAnchorEl(null); }}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              Classic View
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <SystemMotdDialog />
        <Outlet />
      </Box>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} observatoryId={null} />
      <AdminDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} memberships={memberships} orgMemberships={orgMemberships} />
      <ContactSupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
      {Object.entries(floatingWindows).map(([winId, win], index) => (
        <FloatingIframeWindow
          key={winId}
          open
          url={win.url}
          title={win.title}
          width={win.width}
          height={win.height}
          onClose={() => setFloatingWindows(prev => { const n = { ...prev }; delete n[winId]; return n; })}
          offsetIndex={index}
        />
      ))}
    </Box>
  );
};

export default ControlCenterLayout;
